import React from 'react';
import { HiOutlineArrowRight, HiOutlineBolt, HiOutlineArrowPath, HiOutlineShieldCheck } from 'react-icons/hi2';
import { useSupport } from '../../../../contexts/SupportContext';

// Copy here mirrors real backend behaviour: packs are granted on the Stripe
// webhook with a 12-month expiry, and creditService.consumeCredits spends the
// soonest-expiring allocation first.
const ITEMS = [
  {
    icon: HiOutlineBolt,
    title: 'Instant delivery',
    text: 'Credits land in your workspace the moment payment clears. No waiting, no activation step.',
  },
  {
    icon: HiOutlineArrowPath,
    title: 'Nothing goes to waste',
    text: 'Soonest-expiring credits are always spent first, so a pack keeps its full 12 months of shelf life.',
  },
  {
    icon: HiOutlineShieldCheck,
    title: 'Your plan stays the same',
    text: 'A top-up is a one-time payment. It never changes your subscription, renewal date, or price.',
  },
];

const formatNum = (n) => (n || 0).toLocaleString();

// Seeds the support form so the request arrives as a volume enquiry with the
// details support needs, rather than a blank ticket.
const volumeEnquiry = (largestPack) => ({
  category: 'question',
  subject: 'Custom credit volume',
  message:
    `I need more than ${formatNum(largestPack)} credits, or a custom volume rate.\n\n` +
    'Roughly how many credits I would use per month:\n\n\n' +
    'What I am extracting:\n\n',
});

const HowTopUpsWork = ({ largestPack }) => {
  const { openSupport } = useSupport();

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ITEMS.map(({ icon: Icon, title, text }) => (
          <div
            key={title}
            className="flex gap-3.5 rounded-2xl border border-gray-200 dark:border-gray-700/30 bg-white dark:bg-gray-900/40 p-5"
          >
            <div className="flex-shrink-0 w-9 h-9 rounded-xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-sm shadow-cyan-500/15">
              <div className="w-full h-full rounded-[10px] bg-white dark:bg-gray-900 flex items-center justify-center">
                <Icon className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
              </div>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</p>
              <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Volume enquiries — opens the in-app support form, pre-filled */}
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-dashed border-gray-300/80 dark:border-white/[0.1] px-5 py-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Need more than{' '}
          <span className="font-semibold text-gray-800 dark:text-gray-200 tabular-nums">
            {formatNum(largestPack)}
          </span>{' '}
          credits, or a custom volume rate?
        </p>
        <button
          type="button"
          onClick={() => openSupport({ prefill: volumeEnquiry(largestPack) })}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors group"
        >
          Talk to us
          <HiOutlineArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};

export default HowTopUpsWork;
